import AttachFileIcon from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  Step,
  StepLabel,
  Stepper,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { DropzoneAreaBase, FileObject } from "mui-file-dropzone";
import { useEffect, useMemo, useRef, useState } from "react";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
import { importFile, processImports } from "stores/importsUploadStore";
import SelectAccessLevel from "pages/Users/SelectAccessLevel";
import {
  ImportMode,
  OrganizationPortalImportJobTypes,
} from "types/commonTypes";
import {
  organizationUserColumnMappings,
  organizationUserDeactivationColumnMappings,
} from "types/fileColumnMappings";
import { ImportUsersFileUploading } from "./ImportUsersFileUploading";
import { ImportUsersFileUploaded } from "./ImportUsersFileUploaded";
import { ImportUsersModeStep } from "./ImportUsersModeStep";
import { ConfirmDeactivateUsersDialog } from "./ConfirmDeactivateUsersDialog";

// Derived rather than repeated: the mapping is what actually slices each row by index, so a column
// added there must not leave the header check validating a stale list.
const DEACTIVATION_COLUMNS = [...organizationUserDeactivationColumnMappings]
  .sort((a, b) => a.columnIndex! - b.columnIndex!)
  .map((mapping) => mapping.columnName!);

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  title: {
    fontSize: "1.0em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  info: {
    fontSize: "1.0em",
    color: theme.palette.warning.dark,
    textAlign: "center",
    fontWeight: "bold",
    margin: "0 auto",
  },
  sampleLink: {
    alignSelf: "center",
  },
});

type ImportsUsersFormValues = { accessLevelId?: string | null };

type ImportUsersFormProps = {
  onClose: () => void;
  mode?: ImportMode;
  onModeChange: (mode?: ImportMode) => void;
  deactivateDisabledReasonId?: string;
};

const { Form } = withTypes<ImportsUsersFormValues>();

export const ImportUsersForm = ({
  onClose,
  mode,
  onModeChange,
  deactivateDisabledReasonId,
}: ImportUsersFormProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();

  const [activeStep, setActiveStep] = useState(0);

  const [file, setFile] = useState<FileObject | null>(null);
  const [emptyFile, setEmptyFile] = useState<boolean>(true);
  const [accessLevelId, setAccessLevelId] = useState<string | undefined>(
    undefined,
  );

  const [errors, setErrors] = useState<string | undefined>();
  const [confirmingDeactivation, setConfirmingDeactivation] =
    useState<boolean>(false);
  const [validatingFile, setValidatingFile] = useState<boolean>(false);
  const deactivationStarted = useRef<boolean>(false);

  const init = useMemo<ImportsUsersFormValues>(
    () => ({
      accessLevelId: null,
    }),
    [],
  );

  const clearFile = () => {
    setFile(null);
    setEmptyFile(true);
    setAccessLevelId(undefined);
    setErrors(undefined);
    setValidatingFile(false);
  };

  // Switching mode discards the chosen file. Without this, a file picked in add mode — where the CSV
  // header is not checked — would survive a switch to deactivate and skip that check entirely.
  useEffect(() => {
    setFile(null);
    setEmptyFile(true);
    setAccessLevelId(undefined);
    setErrors(undefined);
    setValidatingFile(false);
    setConfirmingDeactivation(false);
    deactivationStarted.current = false;
    setActiveStep(0);
  }, [mode]);

  return useMemo(() => {
    const isDeactivation = mode === "deactivate";

    const steps = [
      t({ id: "selectFile" }),
      t({ id: "fileUpload" }),
      t({ id: "fileUploaded" }),
    ];

    const startAddImport = (values: ImportsUsersFormValues) => {
      processImports(
        file?.file!,
        OrganizationPortalImportJobTypes.OrganizationUser,
        organizationUserColumnMappings,
        (data: any[][]) => {
          let modifiedData = data;
          modifiedData.forEach((x) => (x[3] = values.accessLevelId));
          return modifiedData;
        },
      );
      setActiveStep(1);
    };

    const startDeactivationImport = () => {
      if (deactivationStarted.current) {
        return;
      }

      deactivationStarted.current = true;
      setConfirmingDeactivation(false);
      processImports(
        file?.file!,
        OrganizationPortalImportJobTypes.OrganizationUserDeactivation,
        organizationUserDeactivationColumnMappings,
      );
      setActiveStep(1);
    };

    // Deactivation is confirmed before anything is uploaded, so a mis-clicked destructive import can
    // still be abandoned. The add path keeps submitting straight through, unchanged.
    const onSubmit = (values: ImportsUsersFormValues) => {
      if (isDeactivation) {
        setConfirmingDeactivation(true);
        return;
      }

      startAddImport(values);
    };

    if (mode === undefined) {
      return (
        <ImportUsersModeStep
          onSelect={onModeChange}
          onClose={onClose}
          deactivateDisabledReasonId={deactivateDisabledReasonId}
        />
      );
    }

    return (
      <Box>
        <Box mb={3}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        {activeStep === 0 && (
          <Form
            onSubmit={onSubmit}
            initialValues={init}
            render={({ handleSubmit, submitting }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Box my={1}>
                    {file == null ? (
                      <DropzoneAreaBase
                        acceptedFiles={[
                          ".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values",
                        ]}
                        maxFileSize={150000000}
                        filesLimit={1}
                        onAdd={async (files: FileObject[]) => {
                          let fileObj = files[0];
                          setFile(fileObj);
                          setErrors(undefined);
                          setValidatingFile(true);

                          try {
                            let [result, errorMessageId] = await importFile(
                              fileObj,
                              isDeactivation ? DEACTIVATION_COLUMNS : undefined,
                            );

                            if (result) {
                              setEmptyFile(false);
                              return;
                            }

                            setEmptyFile(true);
                            setFile(null);
                            setErrors(
                              errorMessageId
                                ? t({ id: errorMessageId })
                                : errorMessageId,
                            );
                          } finally {
                            setValidatingFile(false);
                          }
                        }}
                        fileObjects={[]}
                        showAlerts={["error", "info"]}
                        showPreviews={true}
                        showFileNamesInPreview={true}
                        onDelete={(a, b) => setEmptyFile(true)}
                      />
                    ) : (
                      <Card sx={styles.card} variant="outlined">
                        <CardContent>
                          <Grid container direction="row" alignItems="center">
                            <Grid item>
                              <AttachFileIcon />
                            </Grid>
                            <Grid item>
                              <Typography variant="h5" component="div">
                                {t({ id: "fileDetails" })}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box mb={2}></Box>
                          <Typography>{file.file.name}</Typography>
                          <Typography>{file.file.type}</Typography>
                          <Typography>{`${file.file.size} kb`}</Typography>
                        </CardContent>
                        <CardActions>
                          <IconButton onClick={clearFile}>
                            <ClearIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    )}
                  </Box>
                  {!isDeactivation && (
                    <>
                      <Box
                        mt={3}
                        mb={1}
                        display={"flex"}
                        style={{
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="h6" sx={styles.title}>
                          <FormattedMessage id="levelOfAccess" />
                        </Typography>
                      </Box>
                      <Box>
                        <Field name="accessLevelId">
                          {({ input, meta }) => (
                            <FormControl
                              fullWidth
                              required
                              error={meta.error && meta.submitFailed}
                            >
                              <SelectAccessLevel
                                value={accessLevelId}
                                onChange={(value) => {
                                  setAccessLevelId(value);
                                  input.onChange(value);
                                }}
                                required
                              />
                              {meta.error && meta.submitFailed && (
                                <FormHelperText>{meta.error}</FormHelperText>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Box>
                      <Box my={2}>
                        <Alert severity="warning">
                          <FormattedMessage id="accessLevelImportWarningMessage" />
                        </Alert>
                      </Box>
                    </>
                  )}
                  {isDeactivation && (
                    <Box my={2}>
                      <Alert severity="warning">
                        <FormattedMessage id="everyMatchingUserWillBeDeactivated" />
                      </Alert>
                    </Box>
                  )}

                  <Grid
                    container
                    spacing={2}
                    justifyContent="right"
                    mt={2}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Grid item xs={12} sm={12} md={8}>
                      <Link
                        sx={styles.sampleLink}
                        href={
                          isDeactivation
                            ? "/csv_samples/organization_users_deactivation_import.csv"
                            : "/csv_samples/organization_users_import.csv"
                        }
                      >
                        {t({ id: "downloadCsvTemplate" })}
                      </Link>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                      <Button onClick={onClose} fullWidth>
                        {t({ id: "close" })}
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={submitting || emptyFile || validatingFile}
                        autoFocus
                        fullWidth
                      >
                        {t({ id: "import" })}
                      </Button>
                    </Grid>
                  </Grid>
                  {errors && (
                    <Grid container spacing={2} justifyContent="left" my={1}>
                      <Grid item xs={12} sm={12} md={12}>
                        <Alert severity="error">{errors}</Alert>
                      </Grid>
                    </Grid>
                  )}
                </form>
              );
            }}
          />
        )}
        {activeStep === 1 && (
          <Box>
            <ImportUsersFileUploading
              onProcessingFinished={() => {
                setActiveStep(2);
                clearFile();
                deactivationStarted.current = false;
              }}
            />
          </Box>
        )}
        {activeStep === 2 && (
          <Box>
            <ImportUsersFileUploaded
              onClose={onClose}
              onUploadNewFile={() => setActiveStep(0)}
              mode={mode}
            />
          </Box>
        )}
        <ConfirmDeactivateUsersDialog
          open={confirmingDeactivation}
          onConfirm={startDeactivationImport}
          onCancel={() => setConfirmingDeactivation(false)}
        />
      </Box>
    );
  }, [
    activeStep,
    init,
    onClose,
    file,
    styles.card,
    styles.title,
    styles.sampleLink,
    t,
    emptyFile,
    errors,
    accessLevelId,
    validatingFile,
    mode,
    onModeChange,
    deactivateDisabledReasonId,
    confirmingDeactivation,
  ]);
};
